import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Shield,
  DoorOpen,
  ClipboardList,
  Users,
  GraduationCap,
  AlertTriangle,
  BarChart3,
  ChevronRight,
  ChevronDown,
  CalendarDays,
  Sparkles,
  Trash2,
} from 'lucide-react';
import { DesktopMonthCalendar } from '../professor/DesktopMonthCalendar';
import { AdminDesktopAnalyticsCharts } from './AdminDesktopAnalyticsCharts';
import { AdminCalendarDayPanel } from './AdminCalendarDayPanel';
import { ConfirmModal } from '../../components/ui/ConfirmModal';
import type { AdminDashboardPageContext } from './useAdminDashboardPage';
import '../professor/professor-dashboard-desktop.css';
import './admin-dashboard-desktop.css';

type VistaTorre = 'turno' | 'dia';

interface Props {
  ctx: AdminDashboardPageContext;
}

function statusAulaLabel(status: string, totalAlunos?: number): string {
  const s = status.toUpperCase();
  if (s === 'PENDENTE') return 'Falta fazer';
  if (s === 'CONCLUIDA') return 'Concluída';
  if (s === 'ABERTA') return (totalAlunos ?? 0) > 0 ? 'Registrada' : 'Aberta';
  if (s === 'CANCELADA') return 'Cancelada';
  if (s === 'PERDIDA') return 'Perdida';
  return status;
}

type TurmaListaModo = 'todas' | 'criticas';

function turmaEhCritica(
  t: AdminDashboardPageContext['turmasPorTurno'][number]['turmas'][number],
) {
  return Boolean(t.critico ?? (t.temRegistro && t.pct !== null && t.pct < 70));
}

type TurmaRow = AdminDashboardPageContext['turmasPorTurno'][number]['turmas'][number];

function metricsTurma(t: TurmaRow) {
  const r = t.resumoChamada;
  const base = r && r.totalAlunos > 0 ? r.totalAlunos : t.alunos;
  const presentes = r ? r.totalPresentes : t.pres;
  const ausentes = r ? r.totalAusentes : t.aus;
  const atrasados = r?.totalAtrasados ?? 0;
  const justificados = r?.totalJustificados ?? 0;
  const pctPres =
    t.temRegistro && base > 0 ? Math.round((presentes / base) * 100) : null;
  const pctAus =
    t.temRegistro && base > 0 ? Math.round((ausentes / base) * 100) : null;
  return {
    base,
    presentes,
    ausentes,
    atrasados,
    justificados,
    pctPres,
    pctAus,
    numeroAula: r?.numeroAula ?? null,
  };
}

function textoResumoTurma(t: TurmaRow) {
  if (!t.temRegistro) return `${t.alunos} alunos · aguarda 1ª chamada`;
  const m = metricsTurma(t);
  const extras = [
    m.atrasados > 0 ? `${m.atrasados} atrasados` : null,
    m.justificados > 0 ? `${m.justificados} justificados` : null,
  ].filter(Boolean).join(' · ');
  const corpo = `${m.presentes} presentes · ${m.ausentes} ausentes${extras ? ` · ${extras}` : ''} (${m.base} alunos)`;
  if (m.numeroAula != null) return `${m.numeroAula}ª aula: ${corpo}`;
  return corpo;
}

function TurmaRows({
  turmas,
  onOpenChamada,
}: {
  turmas: AdminDashboardPageContext['turmasPorTurno'][number]['turmas'];
  onOpenChamada: () => void;
}) {
  const [abertaId, setAbertaId] = useState<string | null>(null);
  const [modo, setModo] = useState<TurmaListaModo>('todas');
  const turmasKey = turmas.map((t) => t.id).join('|');
  const nCriticas = turmas.filter(turmaEhCritica).length;

  useEffect(() => {
    setAbertaId(null);
  }, [turmasKey, modo]);

  const lista = useMemo(() => {
    const base =
      modo === 'criticas' ? turmas.filter(turmaEhCritica) : [...turmas];
    if (modo === 'criticas') {
      base.sort(
        (a, b) =>
          (a.pct ?? 101) - (b.pct ?? 101) ||
          a.nome.localeCompare(b.nome, 'pt-BR'),
      );
    }
    return base;
  }, [turmas, modo]);

  return (
    <>
      <div className="adm-desk-turma-modo" role="tablist" aria-label="Filtro de turmas">
        <button
          type="button"
          role="tab"
          aria-selected={modo === 'todas'}
          className={`adm-desk-turma-modo-btn${modo === 'todas' ? ' is-on' : ''}`}
          onClick={() => setModo('todas')}
        >
          Todas ({turmas.length})
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={modo === 'criticas'}
          className={`adm-desk-turma-modo-btn${modo === 'criticas' ? ' is-on crit' : ''}`}
          onClick={() => setModo('criticas')}
        >
          Críticas ({nCriticas})
        </button>
      </div>

      {lista.length === 0 ? (
        <p className="adm-desk-turma-empty">
          {modo === 'criticas'
            ? 'Nenhuma turma crítica neste turno.'
            : 'Sem turmas neste turno.'}
        </p>
      ) : null}

      {lista.map((t) => {
        const critico = turmaEhCritica(t);
        const m = metricsTurma(t);
        const pctPresBar = Math.max(0, Math.min(100, m.pctPres ?? 0));
        const pctAusBar = Math.max(0, Math.min(100 - pctPresBar, m.pctAus ?? 0));
        const esperadas = t.chamadasEsperadas ?? t.aulas?.length ?? 0;
        const feitas = t.chamadasFeitas ?? 0;
        const expandida = abertaId === t.id;
        const aulas = t.aulas ?? [];
        const ausentesDia = t.ausentesUnicosDia ?? [];
        const ultimaComChamada = [...aulas]
          .filter((a) => a.chamadaId && (a.status || '').toUpperCase() !== 'PENDENTE')
          .sort((a, b) => b.numeroAula - a.numeroAula)[0];
        const ausentesUltima = ultimaComChamada?.ausentes ?? [];

        return (
          <div
            key={t.id}
            className={`adm-desk-turma-block${expandida ? ' is-open' : ''}${critico ? ' is-crit' : ''}`}
          >
            <button
              type="button"
              className="adm-desk-turma-row"
              aria-expanded={expandida}
              onClick={() => setAbertaId((id) => (id === t.id ? null : t.id))}
            >
              <div className="adm-desk-turma-main">
                <span className="adm-desk-turma-nome">
                  {expandida ? (
                    <ChevronDown size={14} className="adm-desk-turma-chev" aria-hidden />
                  ) : (
                    <ChevronRight size={14} className="adm-desk-turma-chev" aria-hidden />
                  )}
                  {t.nome}
                  {esperadas > 0 ? (
                    <span className="adm-desk-turma-aulas-pill" title="Aulas com chamada / aulas da grade">
                      {feitas}/{esperadas} chamadas
                    </span>
                  ) : null}
                </span>
                <span className="adm-desk-turma-sub">{textoResumoTurma(t)}</span>
                <div
                  className="adm-desk-turma-bar-track"
                  aria-hidden
                  title={
                    m.pctPres != null
                      ? `${m.presentes} presentes (${m.pctPres}%) · ${m.ausentes} ausentes (${m.pctAus}%)`
                      : undefined
                  }
                >
                  {t.temRegistro ? (
                    <>
                      <div
                        className="adm-desk-turma-bar-fill adm-desk-turma-bar-fill--pres"
                        style={{ width: `${pctPresBar}%` }}
                      />
                      <div
                        className="adm-desk-turma-bar-fill adm-desk-turma-bar-fill--aus"
                        style={{ width: `${pctAusBar}%` }}
                      />
                    </>
                  ) : (
                    <div
                      className="adm-desk-turma-bar-fill"
                      style={{ width: '100%', background: '#e2e8f0' }}
                    />
                  )}
                </div>
              </div>
              {!t.temRegistro ? (
                <span className="adm-desk-turma-badge nodata">Sem chamada</span>
              ) : (
                <span
                  className={`adm-desk-turma-badge-split${critico ? ' is-crit' : ''}`}
                  title={`Última chamada: ${m.presentes}/${m.base} presentes · ${m.ausentes}/${m.base} ausentes`}
                >
                  <span className="adm-desk-turma-badge-pres">{m.pctPres}%</span>
                  <span className="adm-desk-turma-badge-aus">
                    {m.pctAus ?? 0}% aus
                  </span>
                  {critico ? (
                    <span className="adm-desk-turma-badge-crit-tag">crítico</span>
                  ) : null}
                </span>
              )}
            </button>

            {expandida ? (
              <div className="adm-desk-turma-detail">
                <p className="adm-desk-turma-detail-lead">
                  <strong>Última chamada:</strong> {textoResumoTurma(t)}
                  {m.pctPres != null
                    ? ` · ${m.pctPres}% presentes / ${m.pctAus}% ausentes`
                    : ''}
                  {esperadas > 0
                    ? ` · Grade: ${feitas}/${esperadas} aulas com chamada`
                    : ''}
                </p>
                {ausentesUltima.length > 0 ? (
                  <div className="adm-desk-turma-ausentes-dia adm-desk-turma-ausentes-dia--ultima">
                    <strong>
                      Ausentes nesta {m.numeroAula != null ? `${m.numeroAula}ª` : 'última'} aula (
                      {ausentesUltima.length})
                    </strong>
                    <ul className="adm-desk-turma-ausentes-list">
                      {ausentesUltima.map((a) => (
                        <li key={a.id}>{a.nome}</li>
                      ))}
                    </ul>
                  </div>
                ) : null}
                {ausentesDia.length > 0 ? (
                  <div className="adm-desk-turma-ausentes-dia">
                    <strong>
                      Ausentes em alguma aula do dia ({ausentesDia.length})
                    </strong>
                    <ul className="adm-desk-turma-ausentes-list">
                      {ausentesDia.map((a) => (
                        <li key={a.id}>{a.nome}</li>
                      ))}
                    </ul>
                  </div>
                ) : t.temRegistro && ausentesUltima.length === 0 ? (
                  <p className="adm-desk-turma-detail-empty">
                    Nenhum ausente nomeado nas chamadas deste dia.
                  </p>
                ) : null}
                {aulas.length === 0 ? (
                  <p className="adm-desk-turma-detail-empty">
                    Sem horários neste dia para esta turma.
                  </p>
                ) : (
                  <ul className="adm-desk-turma-aulas">
                    {aulas.map((aula) => {
                      const st = (aula.status || 'PENDENTE').toUpperCase();
                      const hm =
                        aula.horaInicio && aula.horaFim
                          ? `${aula.horaInicio}–${aula.horaFim}`
                          : aula.horaInicio || '—';
                      const prof = aula.professorNome
                        ? /^prof/i.test(aula.professorNome)
                          ? aula.professorNome
                          : `Prof. ${aula.professorNome}`
                        : 'Sem professor';
                      const stClass =
                        st === 'PENDENTE'
                          ? 'pendente'
                          : st === 'CONCLUIDA' ||
                              (st === 'ABERTA' && (aula.totalAlunos ?? 0) > 0)
                            ? 'concluida'
                            : st.toLowerCase();
                      const ausentesAula = aula.ausentes ?? [];
                      const temCh = Boolean(aula.chamadaId && st !== 'PENDENTE');
                      return (
                        <li
                          key={`${t.id}-${aula.numeroAula}-${aula.chamadaId || 'slot'}`}
                          className={`adm-desk-turma-aula st-${stClass}`}
                        >
                          <div className="adm-desk-turma-aula-head">
                            <span className="adm-desk-turma-aula-num">
                              {aula.numeroAula > 0 ? `${aula.numeroAula}ª` : '—'}
                            </span>
                            <span className="adm-desk-turma-aula-hm">{hm}</span>
                            <span className="adm-desk-turma-aula-mat">
                              {aula.materiaNome || 'Aula'}
                            </span>
                            <span className="adm-desk-turma-aula-prof">{prof}</span>
                            <span className={`adm-desk-turma-aula-st st-${stClass}`}>
                              {statusAulaLabel(st, aula.totalAlunos)}
                            </span>
                          </div>
                          {temCh ? (
                            <div className="adm-desk-turma-aula-meta">
                              {aula.totalPresentes ?? 0} pres. · {aula.totalAusentes ?? ausentesAula.length} aus.
                              {(aula.totalAtrasados ?? 0) > 0
                                ? ` · ${aula.totalAtrasados} atr.`
                                : ''}
                              {(aula.totalJustificados ?? 0) > 0
                                ? ` · ${aula.totalJustificados} just.`
                                : ''}
                              {(aula.totalAlunos ?? 0) > 0
                                ? ` · ${aula.totalAlunos} na chamada`
                                : ''}
                            </div>
                          ) : null}
                          {ausentesAula.length > 0 ? (
                            <ul className="adm-desk-turma-aula-ausentes">
                              {ausentesAula.map((a) => (
                                <li key={a.id}>{a.nome}</li>
                              ))}
                            </ul>
                          ) : temCh ? (
                            <p className="adm-desk-turma-aula-ok">Sem ausentes nesta aula</p>
                          ) : null}
                        </li>
                      );
                    })}
                  </ul>
                )}
                <button
                  type="button"
                  className="adm-desk-turma-detail-cta"
                  onClick={(e) => {
                    e.stopPropagation();
                    onOpenChamada();
                  }}
                >
                  Ver na Chamada
                </button>
              </div>
            ) : null}
          </div>
        );
      })}
    </>
  );
}

export function AdminDashboardDesktop({ ctx }: Props) {
  const navigate = useNavigate();
  const [outrosTurnosAbertos, setOutrosTurnosAbertos] = useState(false);
  const [modalLimparDemoAberto, setModalLimparDemoAberto] = useState(false);
  const {
    nomeEscola,
    user,
    dataFormatada,
    dataCurta,
    diaReferenciaLabel,
    isSimulation,
    hojeStr,
    selectedIso,
    setSelectedIso,
    viewYear,
    setViewYear,
    viewMonth,
    setViewMonth,
    alunosAtivos,
    turmasAtivas,
    profsAtivos,
    censoPorTurno,
    presentesHoje,
    ausentesHoje,
    semRegistroHoje,
    pctPresenca,
    baseAlunosDia,
    semChamadaComPortaria,
    operacaoHojeInsight,
    portariaCount,
    chamadasRealizadas,
    turmasChamadaTotal,
    turnoOperacional,
    presentesTurno,
    ausentesTurno,
    baseAlunosTurno,
    semRegistroTurno,
    pctPresencaTurno,
    chamadasTurnoRealizadas,
    turmasTurnoTotal,
    inconsistenciasTurno,
    portariaTurno,
    temRegistroTurno,
    inconsistencias,
    realtimeConnected,
    fallbackPollMs,
    turnoChartData,
    turmasStatus,
    turmasPorTurno,
    countByDay,
    presencasDiaSel,
    loadingHoje,
    loadingMes,
    loadingDiaSel,
    alertasAtivos,
    riscoChartData,
    totalEmRisco,
    loadingConsolidacao,
  } = ctx;

  const temTurno = Boolean(turnoOperacional && baseAlunosTurno > 0);
  const [vista, setVista] = useState<VistaTorre>(temTurno ? 'turno' : 'dia');

  useEffect(() => {
    if (!temTurno && vista === 'turno') setVista('dia');
  }, [temTurno, vista]);

  const diaSelPresentes = presencasDiaSel.filter(
    (p) => p.status === 'PRESENTE' || p.status === 'ATRASADO',
  ).length;
  const diaSelAusentes = presencasDiaSel.filter((p) => p.status === 'AUSENTE').length;
  const isHoje = selectedIso === hojeStr;
  const diasComDadosNoMes = Object.values(countByDay).filter((n) => n > 0).length;

  const chartPresentesDia = isHoje ? presentesHoje : diaSelPresentes;
  const chartAusentesDia = isHoje ? ausentesHoje : diaSelAusentes;
  const chartSemRegistroDia = isHoje
    ? semRegistroHoje
    : Math.max(0, alunosAtivos - diaSelPresentes - diaSelAusentes);
  const chartBaseDia = isHoje ? baseAlunosDia : alunosAtivos;
  const chartPctDia =
    isHoje && pctPresenca !== null
      ? pctPresenca
      : chartBaseDia > 0
        ? Math.round((chartPresentesDia / chartBaseDia) * 100)
        : 0;

  const chartsLoading = isHoje
    ? loadingHoje || loadingMes || loadingConsolidacao
    : loadingDiaSel || loadingMes || loadingConsolidacao;

  const grupoTurnoAtivo = useMemo(
    () => turmasPorTurno.find((g) => g.isTurnoAtivo) ?? null,
    [turmasPorTurno],
  );
  const outrosGrupos = useMemo(
    () => turmasPorTurno.filter((g) => !g.isTurnoAtivo),
    [turmasPorTurno],
  );

  const turmasTurnoChartData = (grupoTurnoAtivo?.turmas ?? [])
    .slice(0, 8)
    .map((t) => ({
      turma: t.nome.length > 10 ? `${t.nome.slice(0, 9)}…` : t.nome,
      alunos: t.alunos,
    }));

  const modoTurno = vista === 'turno' && temTurno;

  const kpis = modoTurno
    ? {
        portaria: portariaTurno,
        portariaSub: 'RFID neste turno',
        chamadas: chamadasTurnoRealizadas,
        chamadasTotal: turmasTurnoTotal,
        chamadasSub: `turmas · ${turnoOperacional}`,
        presentes: presentesTurno,
        base: baseAlunosTurno,
        semReg: semRegistroTurno,
        pct: pctPresencaTurno,
        ausentes: ausentesTurno,
        inconsist: inconsistenciasTurno,
        inconsistSub: `turno · chamada × RFID`,
        semChamadaHint: !temRegistroTurno,
        temRegistro: temRegistroTurno,
      }
    : {
        portaria: portariaCount,
        portariaSub: 'RFID no dia',
        chamadas: chamadasRealizadas,
        chamadasTotal: turmasChamadaTotal,
        chamadasSub: `turmas no dia · ${turmasAtivas} no censo`,
        presentes: chartPresentesDia,
        base: chartBaseDia,
        semReg: chartSemRegistroDia,
        pct: isHoje ? pctPresenca : chartPctDia,
        ausentes: chartAusentesDia,
        inconsist: inconsistencias,
        inconsistSub: 'no dia · chamada × RFID',
        semChamadaHint: semChamadaComPortaria,
        temRegistro: !semChamadaComPortaria && (presentesHoje + ausentesHoje > 0 || chartPresentesDia + chartAusentesDia > 0),
      };

  const contextoFrase = modoTurno
    ? `Você está olhando o turno ${turnoOperacional}. O dia da escola está em ${
        pctPresenca != null ? `${pctPresenca}%` : '—'
      }${
        semRegistroHoje > 0
          ? ` — ainda há ${semRegistroHoje} alunos sem chamada no dia`
          : ''
      }.`
    : operacaoHojeInsight;

  const heroPct = pctPresenca ?? 0;
  const heroRingLabel =
    pctPresenca !== null
      ? `Presença ${pctPresenca}% no dia`
      : semChamadaComPortaria
        ? `Sem chamada no dia · ${portariaCount} RFID`
        : 'Aguardando chamada no dia';

  const dayPanel = (
    <AdminCalendarDayPanel
      turmasStatus={turmasStatus}
      selectedIso={selectedIso}
      hojeStr={hojeStr}
      alunosAtivos={alunosAtivos}
      presencas={presencasDiaSel}
      loading={loadingDiaSel}
      refetchIntervalMs={fallbackPollMs}
      portariaCount={portariaCount}
    />
  );

  return (
    <div className="admin-page text-[13px] leading-snug dash-desk-page adm-desk-page">
      <div className="dash-desk-root adm-desk-root">
        {ctx.temDadosExemplo && (
          <div
            style={{
              marginBottom: 16,
              padding: '14px 20px',
              borderRadius: 14,
              background: 'linear-gradient(135deg, #fffbeb 0%, #fef3c7 100%)',
              border: '1.5px solid #f59e0b',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 16,
              boxShadow: '0 4px 14px rgba(245, 158, 11, 0.12)',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
              <div
                style={{
                  width: 40,
                  height: 40,
                  borderRadius: 10,
                  background: 'linear-gradient(135deg, #f59e0b, #d97706)',
                  color: '#fff',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                  boxShadow: '0 2px 8px rgba(217, 119, 6, 0.3)',
                }}
              >
                <Sparkles size={20} />
              </div>
              <div>
                <div
                  style={{
                    fontWeight: 800,
                    fontSize: '0.92rem',
                    color: '#92400e',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8,
                  }}
                >
                  <span>Ambiente com Dados de Demonstração Ativos</span>
                  <span
                    style={{
                      fontSize: '0.68rem',
                      padding: '2px 8px',
                      borderRadius: 20,
                      background: '#fde68a',
                      color: '#78350f',
                      fontWeight: 700,
                    }}
                  >
                    AMOSTRA DE TESTE
                  </span>
                </div>
                <div style={{ fontSize: '0.78rem', color: '#b45309', marginTop: 3, lineHeight: 1.4 }}>
                  Esta escola contém registros de exemplo ({ctx.dadosExemploInfo?.alunosDemo ?? 1} aluno,{' '}
                  {ctx.dadosExemploInfo?.profsDemo ?? 1} professor e grade teste) para visualização dos relatórios. Ao
                  iniciar a operação real, você pode remover estes dados com 1 clique mantendo o Diretor, Matérias e Turnos
                  intactos.
                </div>
              </div>
            </div>

            <button
              type="button"
              onClick={() => setModalLimparDemoAberto(true)}
              disabled={ctx.limpandoDadosExemplo}
              style={{
                padding: '10px 18px',
                borderRadius: 10,
                background: 'linear-gradient(135deg, #d97706, #b45309)',
                color: '#fff',
                border: 'none',
                fontWeight: 700,
                fontSize: '0.82rem',
                cursor: ctx.limpandoDadosExemplo ? 'not-allowed' : 'pointer',
                whiteSpace: 'nowrap',
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                boxShadow: '0 2px 8px rgba(180, 83, 9, 0.35)',
                transition: 'all .15s',
              }}
            >
              <Trash2 size={16} />
              {ctx.limpandoDadosExemplo ? 'Removendo...' : 'Limpar Dados de Exemplo'}
            </button>
          </div>
        )}

        <ConfirmModal
          open={modalLimparDemoAberto}
          title="Limpar Dados de Demonstração"
          message="Deseja remover os registros de demonstração (aluno, professor, pai e grade de teste) desta escola? O Diretor, as Matérias BNCC e os Turnos serão preservados, mantendo o sistema pronto para a operação real sem voltar ao modo setup."
          confirmLabel="Sim, Remover Dados Demo"
          cancelLabel="Cancelar"
          variant="warning"
          onConfirm={async () => {
            await ctx.handleLimparDadosExemplo();
            setModalLimparDemoAberto(false);
          }}
          onCancel={() => setModalLimparDemoAberto(false)}
        />

        <header className="adm-desk-hero">
          <div className="adm-desk-hero-bg" aria-hidden />
          <div className="adm-desk-hero-content">
            <span className="adm-desk-hero-badge">
              <Shield size={11} />
              {user?.perfil === 'ESCOLA' ? 'Unidade escolar' : 'Gestão escolar'}
            </span>
            <h1 className="adm-desk-hero-title">{nomeEscola || 'Escola'}</h1>
            <div className="adm-desk-hero-date" aria-label={`${diaReferenciaLabel}: ${dataFormatada}`}>
              <span className={`adm-desk-hero-date-chip${isSimulation ? ' adm-desk-hero-date-chip--sim' : ''}`}>
                <CalendarDays size={12} aria-hidden />
                <span className="adm-desk-hero-date-kind">{diaReferenciaLabel}</span>
                <strong className="adm-desk-hero-date-value">{dataCurta}</strong>
              </span>
              <span className="adm-desk-hero-date-full">{dataFormatada}</span>
              {realtimeConnected ? (
                <span className="adm-desk-live">
                  <i /> Ao vivo
                </span>
              ) : (
                <span className="adm-desk-live adm-desk-live--off">Atualização periódica</span>
              )}
            </div>
            <p className="adm-desk-hero-insight">{contextoFrase}</p>
          </div>
          <div className="adm-desk-hero-ring" aria-label={heroRingLabel} title="Presença da escola no dia">
            <svg viewBox="0 0 36 36" className="adm-desk-ring-svg">
              <path
                className="adm-desk-ring-bg"
                d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
              />
              <path
                className={`adm-desk-ring-fill${semChamadaComPortaria ? ' adm-desk-ring-fill--muted' : ''}`}
                strokeDasharray={`${semChamadaComPortaria || pctPresenca === null ? 0 : heroPct}, 100`}
                d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
              />
            </svg>
            <div className="adm-desk-hero-ring-text">
              <strong>{semChamadaComPortaria || pctPresenca === null ? '—' : `${pctPresenca}%`}</strong>
              <span>No dia</span>
            </div>
          </div>
        </header>

        <div className="dash-desk-top adm-desk-top">
          <div className="dash-desk-greeting">
            <h2 className="adm-desk-section-title">Torre de controle</h2>
            <div
              className="adm-desk-vista-switch"
              role="tablist"
              aria-label="Escopo da torre"
            >
              <button
                type="button"
                role="tab"
                aria-selected={modoTurno}
                className={`adm-desk-vista-btn${modoTurno ? ' is-active' : ''}`}
                disabled={!temTurno}
                onClick={() => setVista('turno')}
              >
                Turno{temTurno ? `: ${turnoOperacional}` : ''}
              </button>
              <button
                type="button"
                role="tab"
                aria-selected={!modoTurno}
                className={`adm-desk-vista-btn${!modoTurno ? ' is-active' : ''}`}
                onClick={() => setVista('dia')}
              >
                Hoje (dia)
              </button>
            </div>
          </div>
          <div className="dash-desk-actions adm-desk-actions">
            <button
              type="button"
              className="dash-desk-action-btn primary"
              onClick={() => navigate('/admin/chamada')}
            >
              <ClipboardList size={12} /> Chamada
            </button>
            <button type="button" className="dash-desk-action-btn" onClick={() => navigate('/admin/turmas')}>
              <GraduationCap size={12} /> Turmas
            </button>
            <button type="button" className="dash-desk-action-btn" onClick={() => navigate('/admin/alunos')}>
              <Users size={12} /> Alunos
            </button>
            <button type="button" className="dash-desk-action-btn" onClick={() => navigate('/admin/acesso')}>
              <DoorOpen size={12} /> Portaria
            </button>
          </div>
        </div>

        <div className="dash-desk-shell adm-desk-shell">
          <div className="dash-desk-main adm-desk-main">
            <section
              className={`adm-desk-focus${modoTurno ? ' adm-desk-focus--turno' : ' adm-desk-focus--dia'}`}
              aria-label={modoTurno ? `Turno ${turnoOperacional}` : 'Dia consolidado'}
            >
              <div
                className="adm-desk-op-strip"
                aria-label={modoTurno ? `KPIs turno ${turnoOperacional}` : 'KPIs do dia'}
              >
                <div className="adm-desk-op-item">
                  <span className="adm-desk-op-lbl">Portaria (RFID)</span>
                  <span className="adm-desk-op-val">
                    {kpis.portaria != null ? kpis.portaria : '—'}
                  </span>
                  <span className="adm-desk-op-sub">{kpis.portariaSub}</span>
                </div>
                <div className="adm-desk-op-item">
                  <span className="adm-desk-op-lbl">Chamadas</span>
                  <span className="adm-desk-op-val">
                    {kpis.chamadas}
                    <small>/{kpis.chamadasTotal}</small>
                  </span>
                  <span className="adm-desk-op-sub">{kpis.chamadasSub}</span>
                </div>
                <div className={`adm-desk-op-item${kpis.semChamadaHint ? ' adm-desk-op-item--hint' : ''}`}>
                  <span className="adm-desk-op-lbl">Na chamada</span>
                  <span className="adm-desk-op-val">
                    {kpis.semChamadaHint || kpis.pct === null ? '—' : `${kpis.pct}%`}
                  </span>
                  <span className="adm-desk-op-sub">
                    {kpis.semChamadaHint
                      ? 'Aguardando lançamento'
                      : `${kpis.presentes}/${kpis.base}${
                          kpis.semReg > 0 ? ` · ${kpis.semReg} s/ chamada` : ''
                        }`}
                  </span>
                </div>
                <div className={`adm-desk-op-item${kpis.inconsist ? ' adm-desk-op-item--warn' : ''}`}>
                  <span className="adm-desk-op-lbl">Inconsistências</span>
                  <span className="adm-desk-op-val">{kpis.inconsist}</span>
                  <span className="adm-desk-op-sub">{kpis.inconsistSub}</span>
                </div>
              </div>

              <p className="adm-desk-fonte-note">
                <strong>Chamada</strong> = professor marcou presente · <strong>RFID</strong> = tag
                lida na portaria — são fontes diferentes.
              </p>

              <AdminDesktopAnalyticsCharts
                mode={modoTurno ? 'turno' : 'dia'}
                scopeLabel={modoTurno ? turnoOperacional! : 'dia'}
                presentes={modoTurno ? presentesTurno : chartPresentesDia}
                ausentes={modoTurno ? ausentesTurno : chartAusentesDia}
                semRegistro={modoTurno ? semRegistroTurno : chartSemRegistroDia}
                pctPresenca={modoTurno ? pctPresencaTurno : isHoje ? pctPresenca : chartPctDia}
                alunosBase={modoTurno ? baseAlunosTurno : chartBaseDia}
                pctComparativo={
                  modoTurno
                    ? pctPresenca
                    : temTurno
                      ? pctPresencaTurno
                      : null
                }
                labelComparativo={
                  modoTurno
                    ? 'No dia (escola)'
                    : temTurno
                      ? `Turno ${turnoOperacional}`
                      : undefined
                }
                compact={modoTurno}
                turnoChartData={turnoChartData}
                riscoChartData={riscoChartData}
                totalEmRisco={totalEmRisco}
                turmasTurnoChartData={turmasTurnoChartData}
                isLoading={chartsLoading}
              />

              {!modoTurno ? (
                <div className="dash-desk-calendar-slot">
                  <DesktopMonthCalendar
                    professorId=""
                    countByDay={countByDay}
                    selectedIso={selectedIso}
                    onSelectIso={setSelectedIso}
                    viewYear={viewYear}
                    viewMonth={viewMonth}
                    onViewChange={(y, m) => {
                      setViewYear(y);
                      setViewMonth(m);
                    }}
                    pickerOnly
                    dayPanel={dayPanel}
                    calendarTitle="Calendário · histórico do dia"
                    calendarSubtitle={
                      loadingMes
                        ? 'Carregando panorama do mês…'
                        : `${diasComDadosNoMes} dia${diasComDadosNoMes !== 1 ? 's' : ''} com dados · visão da escola`
                    }
                    countUnit={{ one: 'reg.', many: 'reg.' }}
                    legendVariant="escola"
                    className="adm-desk-cal dash-desk-cal--compact"
                  />
                </div>
              ) : null}

              <div className="adm-desk-side-card adm-desk-turmas-card">
                <div className="adm-desk-side-card-head">
                  <h3 className="adm-desk-side-title">
                    {modoTurno ? `Turmas · ${turnoOperacional}` : 'Turmas · todos os turnos'}
                  </h3>
                  <button
                    type="button"
                    className="adm-desk-side-link"
                    onClick={() => navigate('/admin/turmas')}
                  >
                    Ver todas
                  </button>
                </div>
                <div className="adm-desk-side-body adm-desk-side-body--flush-x">
                  <div className="adm-desk-turma-scroll">
                    {modoTurno ? (
                      <>
                        {grupoTurnoAtivo ? (
                          <div className="adm-desk-turma-group adm-desk-turma-group--ativo">
                            <div className="adm-desk-turma-group-head">
                              <span className="adm-desk-turma-group-title">
                                {grupoTurnoAtivo.turnoNome}
                                <span className="adm-desk-turma-ativo-pill">ativo</span>
                              </span>
                              <span className="adm-desk-turma-group-meta">
                                {grupoTurnoAtivo.turmas.length} turma
                                {grupoTurnoAtivo.turmas.length !== 1 ? 's' : ''}
                                {' · '}
                                {grupoTurnoAtivo.alunos} alunos
                                {' · '}
                                {grupoTurnoAtivo.comChamada}/{grupoTurnoAtivo.turmas.length} c/
                                chamada
                              </span>
                            </div>
                            <TurmaRows
                              turmas={grupoTurnoAtivo.turmas}
                              onOpenChamada={() => navigate('/admin/chamada')}
                            />
                          </div>
                        ) : (
                          <p className="adm-desk-turma-empty">Sem turmas neste turno.</p>
                        )}

                        {outrosGrupos.length > 0 ? (
                          <div className="adm-desk-turma-outros">
                            <button
                              type="button"
                              className="adm-desk-turma-outros-toggle"
                              aria-expanded={outrosTurnosAbertos}
                              onClick={() => setOutrosTurnosAbertos((v) => !v)}
                            >
                              <span>
                                Ver restante do dia
                                <small>
                                  {outrosGrupos.reduce((s, g) => s + g.turmas.length, 0)} turmas ·{' '}
                                  {outrosGrupos.reduce((s, g) => s + g.alunos, 0)} alunos
                                </small>
                              </span>
                              {outrosTurnosAbertos ? (
                                <ChevronDown size={16} />
                              ) : (
                                <ChevronRight size={16} />
                              )}
                            </button>
                            {outrosTurnosAbertos
                              ? outrosGrupos.map((grupo) => (
                                  <div
                                    key={grupo.turnoId ?? `orfas-${grupo.turnoNome}`}
                                    className="adm-desk-turma-group"
                                  >
                                    <div className="adm-desk-turma-group-head">
                                      <span className="adm-desk-turma-group-title">
                                        {grupo.turnoNome}
                                      </span>
                                      <span className="adm-desk-turma-group-meta">
                                        {grupo.turmas.length} turma
                                        {grupo.turmas.length !== 1 ? 's' : ''}
                                        {' · '}
                                        {grupo.alunos} alunos
                                        {' · '}
                                        {grupo.comChamada}/{grupo.turmas.length} c/ chamada
                                      </span>
                                    </div>
                                    <TurmaRows
                                      turmas={grupo.turmas}
                                      onOpenChamada={() => navigate('/admin/chamada')}
                                    />
                                  </div>
                                ))
                              : null}
                          </div>
                        ) : null}
                      </>
                    ) : (
                      turmasPorTurno.map((grupo) => (
                        <div
                          key={grupo.turnoId ?? `orfas-${grupo.turnoNome}`}
                          className={`adm-desk-turma-group${grupo.isTurnoAtivo ? ' adm-desk-turma-group--ativo' : ''}`}
                        >
                          <div className="adm-desk-turma-group-head">
                            <span className="adm-desk-turma-group-title">
                              {grupo.turnoNome}
                              {grupo.isTurnoAtivo ? (
                                <span className="adm-desk-turma-ativo-pill">ativo agora</span>
                              ) : null}
                            </span>
                            <span className="adm-desk-turma-group-meta">
                              {grupo.turmas.length} turma{grupo.turmas.length !== 1 ? 's' : ''}
                              {' · '}
                              {grupo.alunos} alunos
                              {' · '}
                              {grupo.comChamada}/{grupo.turmas.length} c/ chamada
                            </span>
                          </div>
                          <TurmaRows
                            turmas={grupo.turmas}
                            onOpenChamada={() => navigate('/admin/chamada')}
                          />
                        </div>
                      ))
                    )}
                  </div>
                </div>
              </div>
            </section>
          </div>

          <aside className="dash-desk-sidebar adm-desk-sidebar">
            <div className="adm-desk-side-card adm-desk-side-card--escola">
              <div className="adm-desk-side-card-head">
                <h3 className="adm-desk-side-title">Censo</h3>
                <span className="adm-desk-side-meta">cadastro ativo</span>
              </div>
              <div className="adm-desk-side-body">
                <div className="adm-desk-stat-grid">
                  <button
                    type="button"
                    className="adm-desk-stat-tile"
                    onClick={() => navigate('/admin/alunos')}
                  >
                    <Users size={14} strokeWidth={1.75} />
                    <span className="adm-desk-stat-n">{alunosAtivos}</span>
                    <span className="adm-desk-stat-l">Alunos</span>
                  </button>
                  <button
                    type="button"
                    className="adm-desk-stat-tile"
                    onClick={() => navigate('/admin/turmas')}
                  >
                    <GraduationCap size={14} strokeWidth={1.75} />
                    <span className="adm-desk-stat-n">{turmasAtivas}</span>
                    <span className="adm-desk-stat-l">Turmas</span>
                  </button>
                  <button
                    type="button"
                    className="adm-desk-stat-tile"
                    onClick={() => navigate('/admin/professores')}
                  >
                    <Users size={14} strokeWidth={1.75} />
                    <span className="adm-desk-stat-n">{profsAtivos}</span>
                    <span className="adm-desk-stat-l">Professores</span>
                  </button>
                </div>

                {censoPorTurno.length > 0 ? (
                  <>
                    <p className="adm-desk-side-section-label">Por turno</p>
                    <ul className="adm-desk-censo-turnos">
                      {censoPorTurno.map((row) => (
                        <li
                          key={row.turnoId ?? 'sem-turno'}
                          className={`adm-desk-censo-turno${row.isTurnoAtivo ? ' is-ativo' : ''}`}
                        >
                          <span className="adm-desk-censo-turno-nome">
                            {row.turnoNome}
                            {row.isTurnoAtivo ? (
                              <span className="adm-desk-censo-turno-pill">ativo</span>
                            ) : null}
                          </span>
                          <span className="adm-desk-censo-turno-metrics">
                            <span title="Alunos">
                              <strong>{row.alunos}</strong> alunos
                            </span>
                            <span title="Turmas">
                              <strong>{row.turmas}</strong> turmas
                            </span>
                            {row.professores > 0 ? (
                              <span title="Professores na grade do dia">
                                <strong>{row.professores}</strong> profs
                              </span>
                            ) : null}
                          </span>
                        </li>
                      ))}
                    </ul>
                  </>
                ) : null}
              </div>
            </div>

            <div
              className={`adm-desk-side-card adm-desk-side-card--alert${alertasAtivos > 0 || inconsistencias > 0 ? ' adm-desk-side-card--alert-active' : ''}`}
            >
              <div className="adm-desk-side-card-head">
                <h3 className="adm-desk-side-title">Alertas</h3>
                {alertasAtivos > 0 ? (
                  <span className="adm-desk-alert-count">{alertasAtivos}</span>
                ) : null}
              </div>
              <div className="adm-desk-side-body">
                {alertasAtivos > 0 ? (
                  <>
                    <p className="adm-desk-alert-desc">
                      Eventos ativos na portaria (RFID) ou inconsistências que exigem conferência.
                    </p>
                    <button
                      type="button"
                      className="adm-desk-alert-cta"
                      onClick={() => navigate('/admin/acesso')}
                    >
                      Abrir portaria
                      <ChevronRight size={14} />
                    </button>
                  </>
                ) : (
                  <p className="adm-desk-alert-desc adm-desk-alert-desc--ok">
                    Nenhum alerta crítico no momento.
                  </p>
                )}
                {inconsistencias > 0 ? (
                  <button
                    type="button"
                    className="adm-desk-info-row adm-desk-info-row--warn adm-desk-info-row--compact"
                    onClick={() => navigate('/admin/chamadas-gestao')}
                  >
                    <span className="adm-desk-info-icon adm-desk-info-icon--orange">
                      <AlertTriangle size={13} />
                    </span>
                    <span className="adm-desk-info-text">
                      <span className="adm-desk-info-title">Inconsistências no dia</span>
                      <span className="adm-desk-info-sub">Chamada × RFID</span>
                    </span>
                    <span className="adm-desk-info-end">
                      <span className="adm-desk-info-val">{inconsistencias}</span>
                    </span>
                  </button>
                ) : null}
              </div>
            </div>

            <div className="adm-desk-side-card adm-desk-side-card--atalhos">
              <div className="adm-desk-side-card-head">
                <h3 className="adm-desk-side-title">Atalhos</h3>
                <span className="adm-desk-side-meta">
                  {realtimeConnected ? 'Ao vivo' : 'atualização periódica'}
                </span>
              </div>
              <div className="adm-desk-side-body">
                <div className="adm-desk-atalhos-grid">
                  <button type="button" onClick={() => navigate('/admin/chamada')}>
                    <ClipboardList size={14} /> Chamada
                  </button>
                  <button type="button" onClick={() => navigate('/admin/acesso')}>
                    <DoorOpen size={14} /> Portaria
                    <small>{portariaCount} no dia</small>
                  </button>
                  <button type="button" onClick={() => navigate('/admin/turmas')}>
                    <GraduationCap size={14} /> Turmas
                  </button>
                  <button type="button" onClick={() => navigate('/admin/chamadas-gestao')}>
                    <BarChart3 size={14} /> Governança
                  </button>
                </div>
              </div>
            </div>
          </aside>
        </div>
      </div>
    </div>
  );
}
